import React, { type ReactNode, useState } from 'react';

import { PlusIcon } from '../icons/Plus';

export type AccordionItem = {
    question: string;
    answer: ReactNode;
};

type AccordionProps = {
    items: AccordionItem[];
    /** BEM-style class prefix, e.g. "fa-faq" or "fb-about". */
    classPrefix: string;
    /** Prefix for generated id attributes (button + panel). */
    idPrefix: string;
    /** Index that should start expanded. Only one row is open at a time. */
    initialExpandedIndex?: number | null;
    /** Typography utility class for answer paragraphs. Defaults to body--small. */
    answerClass?: string;
};

type RowProps = {
    item: AccordionItem;
    index: number;
    expanded: boolean;
    onToggle: () => void;
    classPrefix: string;
    idPrefix: string;
    answerClass: string;
};

function AccordionRow({ item, index, expanded, onToggle, classPrefix, idPrefix, answerClass }: RowProps) {
    const panelId = `${idPrefix}-panel-${index}`;
    const buttonId = `${idPrefix}-button-${index}`;
    return (
        <div className={`${classPrefix}__row${expanded ? ` ${classPrefix}__row--expanded` : ''}`}>
            <button
                id={buttonId}
                type="button"
                className={`${classPrefix}__toggle`}
                onClick={onToggle}
                aria-expanded={expanded}
                aria-controls={panelId}
            >
                <span className={`${classPrefix}__question faves-h3`}>{item.question}</span>
                <PlusIcon
                    className={`${classPrefix}__icon${expanded ? ` ${classPrefix}__icon--close` : ''}`}
                />
            </button>
            <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                className={`${classPrefix}__answer-wrap`}
                aria-hidden={!expanded}
            >
                <div className={`${classPrefix}__answer-inner`}>
                    <div className={`${classPrefix}__answer ${answerClass}`}>{item.answer}</div>
                </div>
            </div>
        </div>
    );
}

export function Accordion({ items, classPrefix, idPrefix, initialExpandedIndex = null, answerClass = 'faves-body--small' }: AccordionProps) {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(() => initialExpandedIndex);

    const toggle = (i: number) => {
        setExpandedIndex((prev) => (prev === i ? null : i));
    };

    return (
        <div className={`${classPrefix}__list`}>
            {items.map((item, i) => (
                <AccordionRow
                    key={i}
                    item={item}
                    index={i}
                    expanded={i === expandedIndex}
                    onToggle={() => toggle(i)}
                    classPrefix={classPrefix}
                    idPrefix={idPrefix}
                    answerClass={answerClass}
                />
            ))}
        </div>
    );
}
